import {
  NgModule,
  Provider,
  inject,
  provideAppInitializer,
} from '@angular/core';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication,
} from '@azure/msal-browser';
import { MsalAuthenticationService } from './msal-auth.service';
import {
  CookieService,
  MetaDataService,
  UserTrackingService,
  WindowRef,
} from '@boels/helpers';
import { SharedStateModule } from '@boels/shared-state';
import { BrowserModule } from '@angular/platform-browser';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { AppConfig } from '@boels/models';

function initializeUserAccount(
  authService: MsalAuthenticationService,
): () => void {
  return () => {
    return authService.initializeMsal();
  };
}

function handleBfAction(winRef: WindowRef): () => void {
  return () => {
    if (winRef.isBrowser()) {
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) {
          winRef.removeAuthKeyPairsFromSession();
          window.location.reload();
        }
      });
    }
  };
}

function msalInstanceFactory(environment: AppConfig): IPublicClientApplication {
  return new PublicClientApplication({
    auth: environment.msalConfig.instance.auth,
    cache: environment.msalConfig.instance.cache,
    system: {
      ...environment.msalConfig.instance.system,
      loadFrameTimeout: 10000,
    },
  });
}

export function msalGuardConfigFactory(
  environment: AppConfig,
): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['openid', environment.msalConfig.instance.auth.clientId],
    },
  };
}

function msalInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', [
    'user.read',
  ]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

const msalInterceptors: Provider[] = [
  {
    provide: HTTP_INTERCEPTORS,
    useClass: MsalInterceptor,
    multi: true,
  },
];

@NgModule({
  imports: [BrowserModule, SharedStateModule],
  providers: [
    ...msalInterceptors,
    {
      provide: MSAL_INSTANCE,
      useFactory: () => {
        const environment = inject<AppConfig>('ENVIRONMENT' as any);
        return msalInstanceFactory(environment);
      },
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: () => {
        const environment = inject<AppConfig>('ENVIRONMENT' as any);
        return msalGuardConfigFactory(environment);
      },
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: msalInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    MetaDataService,
    CookieService,
    UserTrackingService,
    provideAppInitializer(() => {
      const initializerFn = initializeUserAccount(
        inject(MsalAuthenticationService),
      );
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = handleBfAction(inject(WindowRef));
      return initializerFn();
    }),
  ],
})
export class AuthenticationModule {}
